/**
 * MOBILISATION ADVANCES.
 *
 * Money paid to a contractor BEFORE he has done anything, so he can get plant,
 * labour and a camp onto site. It is recovered out of his running account
 * bills at an agreed rate until it is back.
 *
 * WHAT THIS SCREEN EXISTS TO FIX — found 21-09-2026
 *
 * Until 0163 the advance was a percentage on the work order and nothing else.
 * fn_wo_advance_outstanding computed it as contract_value × advance_pct, and
 * every RA bill DEDUCTED against that figure. The system was recovering money
 * it had no record of ever paying. Four live orders were in that state, one of
 * them already recovered in full: ₹10,000 taken back off BIPLAB SHARMA's bills
 * against an advance nothing in the database says he ever received.
 *
 * A percentage in a contract is an ENTITLEMENT. Money is a PAYMENT. This
 * screen keeps the two apart and shows both, because the gap between them is
 * the only thing that tells you whether somebody has been short-paid.
 *
 * THE RECONCILIATION TILE IS THE POINT
 *
 * "Recovered but never paid" is the number nobody had. If it is anything other
 * than zero, either an advance went out before IMS and needs regularising, or
 * a contractor is owed money. Both are actionable, and both were invisible.
 *
 * WHY THE GUARANTEE IS CHECKED AT PAYMENT AND NOT AT RAISING
 *
 * A bank takes a week to issue one. Blocking the approval chain on it would
 * mean the CMD signing nothing for a week while the paper is in the post. What
 * must never happen is the MONEY leaving without it, so that is where the
 * refusal sits — in rpc_adv_record_payment, and mirrored here.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Banknote, CalendarClock, CheckCircle2, FilterX,
  Landmark, Scale, Send, ShieldCheck, Trash2, TrendingDown, Wallet, FileDown,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, inr, rupees, localIso } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select,
  Stat, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { ApprovalChain } from '../components/ApprovalChain'

const n = (v: unknown) => Number(v ?? 0) || 0

/** Mirrors rpc_adv_raise. The RPC is the enforcement; this is the door. */
const MAY_RAISE = ['admin', 'accounts_spoc', 'procurement_head', 'procurement_manager',
                   'project_head', 'project_manager']
/** Mirrors rpc_adv_record_payment and rpc_adv_regularise. */
const MAY_PAY = ['admin', 'accounts_spoc']

interface AdvRow {
  id: string; wo_id: string; wo_title: string | null
  site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null; vendor_pan: string | null
  amount: number; purpose: string; remarks: string | null
  entitled_at_raising: number; paid_before: number; recovery_pct: number
  bg_required: boolean; bg_number: string | null; bg_bank: string | null
  bg_amount: number | null; bg_expires_on: string | null
  bg_returned_on: string | null; bg_return_note: string | null
  bg_live: boolean; bg_days_to_expiry: number | null
  paid_outside_ims: boolean; regularisation_note: string | null
  status: string; current_step: number
  paid_on: string | null; mode: string | null; reference_no: string | null
  is_paid: boolean
  created_by: string | null; raised_by_name: string | null; creator_email: string | null
  approved_by: string | null; approved_by_name: string | null
  approval_date: string | null
  return_reason: string | null; rejection_reason: string | null
  date_created: string; created_at: string
  entitled_now: number; paid_now: number; outstanding_now: number
  attachment_count: number
}

interface PosRow {
  wo_id: string; site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null; title: string | null
  wo_status: string; contract_value: number
  advance_pct: number; advance_recovery_pct: number
  entitled: number; paid: number; recovered: number; outstanding: number
  unclaimed: number; over_recovered: number
  in_chain: number; guarantees_live: number
}

/* ========================================================================== */

const BLANK = { term: '', status: '', vendor: '' }

export function AdvanceList() {
  const { profile } = useAuth()
  const toast = useToast()
  const [f, setF] = useState({ ...BLANK })
  const [raising, setRaising] = useState<PosRow | null>(null)
  const [regularising, setRegularising] = useState<PosRow | null>(null)
  const { sort, toggle } = useSort('date_created', 'desc')

  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.values(f).some(Boolean)

  const { data: rows, loading: l1, refresh: r1 } = useQuery<AdvRow>(
    'v_wo_advances',
    () => supabase.from('v_wo_advances').select('*')
      .order('created_at', { ascending: false }).limit(1000),
    [], { cacheable: false })

  const { data: position, loading: l2, refresh: r2 } = useQuery<PosRow>(
    'v_wo_advance_position',
    () => supabase.from('v_wo_advance_position').select('*').order('wo_id'),
    [], { cacheable: false, idField: 'wo_id' })

  const { data: settings, refresh: r3 } = useQuery<{ key: string; value: string; description: string | null }>(
    'service_settings_adv',
    () => supabase.from('service_settings').select('key, value, description')
      .eq('key', 'advance_bg_threshold'),
    [], { cacheable: false, idField: 'key' })

  const refresh = () => { r1(); r2(); r3() }
  const canRaise = MAY_RAISE.includes(profile?.role ?? '')
  const canPay = MAY_PAY.includes(profile?.role ?? '')
  const threshold = n(settings[0]?.value) || 10000000

  const vendors = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of rows) m.set(r.vendor_id, r.vendor_name ?? r.vendor_id)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [rows])

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    return rows.filter((r) =>
      (!f.status || r.status === f.status)
      && (!f.vendor || r.vendor_id === f.vendor)
      && (!t || r.id.toLowerCase().includes(t) || r.wo_id.toLowerCase().includes(t)
          || (r.vendor_name ?? '').toLowerCase().includes(t)
          || (r.purpose ?? '').toLowerCase().includes(t)
          || (r.bg_number ?? '').toLowerCase().includes(t)))
  }, [rows, f])

  const sorted = useMemo(() => sortRows(shown, sort), [shown, sort])

  const totalPaid = position.reduce((a, p) => a + n(p.paid), 0)
  const outstanding = position.reduce((a, p) => a + n(p.outstanding), 0)
  const unclaimed = position.filter((p) => n(p.unclaimed) > 0.5)
  const orphaned = position.filter((p) => n(p.over_recovered) > 0.5)
  const inChain = rows.filter((r) => ['DRAFT', 'SUBMITTED'].includes(r.status))

  // A guarantee that lapses while the advance is still out leaves OVAL naked.
  const expiring = rows.filter((r) => r.bg_live
    && r.bg_days_to_expiry !== null && r.bg_days_to_expiry <= 45)

  if (l1 || l2) return <Loading label="Reading the advances…" />

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Mobilisation Advances</h1>
        <p className="text-sm text-slate-500">
          Money paid to a contractor before any work is measured, and recovered out of
          his running account bills. Numbered <strong>ADV-</strong>, approved by the
          Project Head, the Procurement Manager and the CMD.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Paid out, all time" value={rupees(totalPaid)}
              sub={`across ${position.filter((p) => n(p.paid) > 0).length} order(s)`}
              icon={<Banknote className="h-4 w-4" />} />
        <Stat label="Still to recover" value={rupees(outstanding)}
              tone={outstanding > 0 ? 'warn' : 'good'}
              sub="comes back off future bills"
              icon={<TrendingDown className="h-4 w-4" />} />
        <Stat label="Entitled, not taken" value={rupees(unclaimed.reduce((a, p) => a + n(p.unclaimed), 0))}
              sub={`${unclaimed.length} order(s) could still draw one`}
              icon={<Landmark className="h-4 w-4" />} />
        {/*
          THE NUMBER NOBODY HAD. Bills have clawed back more than the system
          can show ever leaving the bank.
        */}
        <Stat label="Recovered but never paid"
              value={rupees(orphaned.reduce((a, p) => a + n(p.over_recovered), 0))}
              tone={orphaned.length ? 'bad' : 'good'}
              sub={orphaned.length
                ? `${orphaned.length} order(s) — needs explaining`
                : 'every rupee accounted for'}
              icon={orphaned.length
                ? <AlertTriangle className="h-4 w-4" />
                : <CheckCircle2 className="h-4 w-4" />} />
      </div>

      {orphaned.length > 0 && (
        <Alert tone="error" title="Bills have recovered advance money that was never recorded as paid">
          <p>
            On {orphaned.map((p) => p.wo_id).join(', ')}, running account bills have deducted{' '}
            <strong>{rupees(orphaned.reduce((a, p) => a + n(p.over_recovered), 0))}</strong>{' '}
            of advance against payments this system has no record of. Either the money went
            out before IMS and needs recording, or the contractor has been short-paid.
          </p>
          <p className="mt-1.5 text-xs">
            Nothing is being deducted any more — since 0163 a bill recovers only what was
            actually paid. {canPay
              ? 'Use “Record one paid outside IMS” on the order below to regularise it.'
              : 'Accounts can regularise it from this screen.'}
          </p>
        </Alert>
      )}

      {expiring.length > 0 && (
        <Alert tone="warning" title={`${expiring.length} bank guarantee(s) expiring soon`}>
          {expiring.map((r, i) => (
            <span key={r.id}>
              {i > 0 && '; '}
              <Link to={`/service/advances/${r.id}`} className="font-medium underline">{r.id}</Link>
              {' '}— {r.bg_number} at {r.bg_bank}, expires {ddmmyyyy(r.bg_expires_on)}
              {' '}({r.bg_days_to_expiry} day{r.bg_days_to_expiry === 1 ? '' : 's'})
            </span>
          ))}
          . A guarantee that lapses while the advance is still out leaves OVAL unsecured —
          get it extended, or recover the balance first.
        </Alert>
      )}

      {inChain.length > 0 && (
        <Alert tone="info" title={`${rupees(inChain.reduce((a, r) => a + n(r.amount), 0))} is in the chain`}>
          {inChain.map((r, i) => (
            <span key={r.id}>
              {i > 0 && ', '}
              <Link to={`/service/advances/${r.id}`} className="font-medium underline">{r.id}</Link>
              {' '}({rupees(r.amount)} on {r.wo_id}, {r.status.toLowerCase()})
            </span>
          ))}
          . That much counts against what may still be advanced on those orders, so the same
          money cannot be asked for twice.
        </Alert>
      )}

      {/* ------------------------------------------------------------------ */}
      <Card title="Where each order stands"
            subtitle="What the contract allows, what actually went out, and what the bills have taken back.">
        {position.length === 0 ? (
          <EmptyState icon={<Landmark className="h-8 w-8" />}
                      title="No order carries an advance provision"
                      message="An advance has to be in the contract before it can be paid — set the advance percentage on the work order." />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Work order</th><th>Contractor</th>
                  <th className="num">Contract allows</th>
                  <th className="num">Actually paid</th>
                  <th className="num">Recovered</th>
                  <th className="num">Still out</th>
                  <th>Note</th>
                  {(canRaise || canPay) && <th />}
                </tr>
              </thead>
              <tbody>
                {position.map((p) => (
                  <tr key={p.wo_id} className={n(p.over_recovered) > 0.5 ? 'bg-red-50/50' : ''}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/work-orders/${p.wo_id}`}
                            className="text-amber-700 hover:underline">{p.wo_id}</Link>
                      <span className="block max-w-[190px] truncate text-[11px] text-slate-400">
                        {p.title}
                      </span>
                    </td>
                    <td className="max-w-[160px] truncate">
                      {p.vendor_name}
                      <span className="block truncate text-[11px] text-slate-400">{p.site_name}</span>
                    </td>
                    <td className="num whitespace-nowrap">
                      {rupees(p.entitled)}
                      <span className="block text-[11px] text-slate-400">{inr(p.advance_pct, 2)}%</span>
                    </td>
                    <td className="num whitespace-nowrap font-semibold">{rupees(p.paid)}</td>
                    <td className="num whitespace-nowrap text-slate-600">{rupees(p.recovered)}</td>
                    <td className="num whitespace-nowrap font-semibold">{rupees(p.outstanding)}</td>
                    <td className="max-w-[210px] text-xs">
                      {n(p.over_recovered) > 0.5 ? (
                        <span className="text-red-700">
                          {rupees(p.over_recovered)} recovered against nothing on record
                        </span>
                      ) : n(p.unclaimed) > 0.5 ? (
                        <span className="text-slate-500">
                          {rupees(p.unclaimed)} could still be drawn
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    {(canRaise || canPay) && (
                      <td className="whitespace-nowrap text-right">
                        <div className="flex justify-end gap-1.5">
                          {canRaise && n(p.unclaimed) > 0.5
                            && ['VENDACK', 'PARTIALLY_FULFILLED', 'FULFILLED'].includes(p.wo_status) && (
                            <button className="btn-primary btn-sm" onClick={() => setRaising(p)}>
                              <Banknote className="h-4 w-4" /> Advance
                            </button>
                          )}
                          {canPay && (
                            <button className="btn-secondary btn-sm" onClick={() => setRegularising(p)}
                                    title="Record an advance that was paid before this order was on IMS">
                              <CalendarClock className="h-4 w-4" /> Paid outside
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------------------ */}
      <Card
        title="Every advance"
        subtitle="Whatever became of it. A withdrawn or rejected one frees the entitlement again."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                         placeholder="ADV, work order, contractor, BG number…" />
            <Select className="w-auto" value={f.status}
                    onChange={(e) => set({ status: e.target.value })}
                    placeholder="Any status"
                    options={['DRAFT', 'SUBMITTED', 'APPROVED', 'RETURNED', 'REJECTED', 'CANCELLED']
                      .map((v) => ({ value: v, label: v }))} />
            <Select className="w-auto" value={f.vendor}
                    onChange={(e) => set({ vendor: e.target.value })}
                    placeholder="All contractors"
                    options={vendors.map(([id, name]) => ({ value: id, label: name }))} />
            {dirty && (
              <button className="btn-ghost btn-sm" onClick={() => setF({ ...BLANK })}>
                <FilterX className="h-4 w-4" /> Clear
              </button>
            )}
          </div>
        }
      >
        {sorted.length === 0 ? (
          <EmptyState icon={<Banknote className="h-8 w-8" />}
                      title={dirty ? 'Nothing matches' : 'No advance has been raised yet'}
                      message={dirty
                        ? 'Clear the filters to see everything.'
                        : 'Use the Advance button on an order above to start one.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Advance</Th>
                  <Th id="date_created" sort={sort} onSort={toggle}>Raised</Th>
                  <th>Work order</th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Contractor</Th>
                  <Th id="amount" sort={sort} onSort={toggle} align="right">Amount</Th>
                  <th>Guarantee</th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/advances/${r.id}`}
                            className="text-amber-700 hover:underline">{r.id}</Link>
                      {r.paid_outside_ims && (
                        <span className="block text-[11px] text-slate-400">paid outside IMS</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(r.date_created)}</td>
                    <td className="whitespace-nowrap text-xs">
                      <Link to={`/service/work-orders/${r.wo_id}`}
                            className="font-medium text-amber-700 hover:underline">{r.wo_id}</Link>
                    </td>
                    <td className="max-w-[160px] truncate">{r.vendor_name}</td>
                    <td className="num whitespace-nowrap font-semibold">{rupees(r.amount)}</td>
                    <td className="whitespace-nowrap text-xs">
                      {r.bg_number
                        ? r.bg_returned_on
                          ? <span className="pill-green">returned</span>
                          : <span className="pill-amber" title={`${r.bg_bank} · expires ${ddmmyyyy(r.bg_expires_on)}`}>
                              {r.bg_number}
                            </span>
                        : r.bg_required
                          ? <span className="pill-red">required</span>
                          : <span className="text-slate-400">not needed</span>}
                    </td>
                    <td><StatusPill status={r.status} /></td>
                    <td className="whitespace-nowrap text-xs">
                      {r.is_paid
                        ? <span className="pill-green">{ddmmyyyy(r.paid_on)}</span>
                        : r.status === 'APPROVED'
                          ? <span className="pill-amber">awaiting payment</span>
                          : <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------------------
          THE THRESHOLD, WHERE THE RULE IS USED.

          Board instruction, 21-09-2026: ten lakh, maintainable by the System
          Administrator. It sits here rather than in a settings screen nobody
          opens, because the person who wonders what the rule is is the person
          looking at advances.

          It is a SETTING and not a constant, so fn_advance_bg_threshold reads
          it live. bg_required is frozen onto each advance when it is raised,
          so moving this line does not retrospectively free a man who already
          lodged a guarantee, nor impose one on an advance raised before.
      ------------------------------------------------------------------ */}
      <BgThreshold value={threshold} onChanged={refresh} />

      <RaiseModal order={raising} onClose={() => setRaising(null)}
                  onDone={() => { setRaising(null); refresh(); toast.success('Advance raised.') }} />
      <RegulariseModal order={regularising} onClose={() => setRegularising(null)}
                       onDone={() => { setRegularising(null); refresh(); toast.success('Recorded.') }} />
    </div>
  )
}

/* ========================================================================== */
/*  THE BANK GUARANTEE THRESHOLD                                              */
/* ========================================================================== */

function BgThreshold({ value, onChanged }: { value: number; onChanged: () => void }) {
  const { profile } = useAuth()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))
  const [busy, setBusy] = useState(false)

  const isAdmin = profile?.role === 'admin'
  const asked = Number(draft) || 0

  const save = async () => {
    setBusy(true)
    const { error } = await supabase.from('service_settings')
      .update({ value: String(Math.round(asked)), updated_at: new Date().toISOString() })
      .eq('key', 'advance_bg_threshold')
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not change it.')); return }
    setEditing(false); onChanged()
    toast.success(`A guarantee is now required at or above ${rupees(asked)}.`)
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-600">
          <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />
          <strong>A bank guarantee is required at or above {rupees(value)}.</strong>{' '}
          Below that an advance may be paid without one. The requirement is fixed onto each
          advance when it is raised, so changing this affects advances raised from now on.
        </p>
        {isAdmin && !editing && (
          <button className="btn-ghost btn-sm" onClick={() => { setDraft(String(value)); setEditing(true) }}>
            <Scale className="h-4 w-4" /> Change it
          </button>
        )}
      </div>

      {isAdmin && editing && (
        <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-slate-200 pt-3">
          <Field label="Guarantee required at or above"
                 hint="Board instruction, 21-09-2026: ten lakh."
                 className="w-56">
            <Input type="number" min="0" step="1000" value={draft}
                   onChange={(e) => setDraft(e.target.value)} />
          </Field>
          <div className="flex gap-2 pb-1">
            <button className="btn-primary btn-sm" disabled={busy || asked <= 0}
                    onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-ghost btn-sm" disabled={busy}
                    onClick={() => setEditing(false)}>Cancel</button>
          </div>
          {asked > 0 && asked !== value && (
            <p className="pb-2 text-xs text-amber-700">
              From {rupees(value)} to {rupees(asked)}.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/* ========================================================================== */
/*  RAISING ONE                                                               */
/* ========================================================================== */

function RaiseModal({ order, onClose, onDone }: {
  order: PosRow | null; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [amount, setAmount] = useState('')
  const [purpose, setPurpose] = useState('')
  const [bg, setBg] = useState({ number: '', bank: '', amount: '', expires: '' })
  const [busy, setBusy] = useState(false)

  // Read the threshold from the database rather than repeating 1,00,000 here.
  const { data: settings } = useQuery<{ key: string; value: string }>(
    'service_settings_bg',
    () => supabase.from('service_settings').select('key, value')
      .eq('key', 'advance_bg_threshold'),
    [], { cacheable: false, idField: 'key' })
  const threshold = n(settings[0]?.value) || 100000

  if (!order) return null

  const ceiling = n(order.unclaimed)
  const asked = Number(amount) || 0
  const over = asked > ceiling + 0.004
  const needsBg = asked >= threshold
  const bgStarted = Object.values(bg).some((v) => v.trim())
  const bgComplete = bg.number.trim() && bg.bank.trim() && bg.amount.trim() && bg.expires
  const ready = asked > 0 && !over && purpose.trim().length > 0
    && (!bgStarted || bgComplete)

  const submit = async () => {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_adv_raise', {
      p_wo_id: order.wo_id,
      p_amount: asked,
      p_purpose: purpose.trim(),
      p_bg_number: bg.number.trim() || null,
      p_bg_bank: bg.bank.trim() || null,
      p_bg_amount: bg.amount.trim() ? Number(bg.amount) : null,
      p_bg_expires_on: bg.expires || null,
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not raise the advance.')); return }
    setAmount(''); setPurpose(''); setBg({ number: '', bank: '', amount: '', expires: '' })
    onDone()
  }

  return (
    <Modal open onClose={onClose} wide
           title={`Mobilisation advance on ${order.wo_id}`}
           subtitle={`${order.vendor_name ?? order.vendor_id} — ${order.title ?? ''}`}
           footer={
             <>
               <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
               <button className="btn-primary" onClick={submit} disabled={!ready || busy}>
                 {busy ? 'Raising…' : 'Raise the advance'}
               </button>
             </>
           }>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Figure label="The contract allows" value={rupees(order.entitled)}
                  sub={`${inr(order.advance_pct, 2)}% of ${rupees(order.contract_value)}`} />
          <Figure label="Already paid or in the chain" value={rupees(n(order.entitled) - ceiling)} />
          <Figure label="You can advance up to" value={rupees(ceiling)} tone="warn" />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
          <TrendingDown className="mr-1 inline h-3.5 w-3.5" />
          <strong>How it comes back.</strong> Every running account bill on this order will
          recover <strong>{inr(order.advance_recovery_pct, 2)}%</strong> of its gross value
          until the advance is repaid in full. That rate is taken off the order and frozen
          onto this advance, so changing the order later cannot rewrite a schedule the
          contractor has already agreed to.
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount" required
                 error={over ? `The contract leaves only ${rupees(ceiling)}.` : undefined}>
            <Input type="number" min="0" step="0.01" value={amount} invalid={over}
                   onChange={(e) => setAmount(e.target.value)} placeholder={inr(ceiling)} />
          </Field>
          <Field label="Quick fill" hint="Edit freely">
            <button className="btn-ghost btn-sm" onClick={() => setAmount(String(ceiling))}>
              The full entitlement
            </button>
          </Field>
        </div>

        <Field label="What it is for" required
               hint="Kept permanently. This is money paid before anything has been measured.">
          <Textarea rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)}
                    placeholder="Plant and labour camp to site before the first pour." />
        </Field>

        {/* ---------------------------------------------------------------
            THE GUARANTEE. Compulsory at PAYMENT, not here — the bank takes a
            week and the chain should not wait on the post. Saying so plainly
            is better than a refusal the user cannot act on today.
        --------------------------------------------------------------- */}
        <div className={`rounded-lg border px-4 py-3 ${
          needsBg ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
          <p className="mb-2 text-xs">
            <ShieldCheck className={`mr-1 inline h-3.5 w-3.5 ${needsBg ? 'text-amber-700' : 'text-slate-400'}`} />
            {needsBg ? (
              <>
                <strong className="text-amber-900">A bank guarantee is required.</strong>{' '}
                <span className="text-amber-800">
                  {rupees(asked)} is at or above the {rupees(threshold)} threshold. You can
                  raise the advance now and add the guarantee before Accounts pays it — but
                  the money will not go out without it.
                </span>
              </>
            ) : (
              <span className="text-slate-600">
                No guarantee is required below {rupees(threshold)}. Record one anyway if you
                have taken it.
              </span>
            )}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Guarantee number">
              <Input value={bg.number} onChange={(e) => setBg({ ...bg, number: e.target.value })} />
            </Field>
            <Field label="Issuing bank">
              <Input value={bg.bank} onChange={(e) => setBg({ ...bg, bank: e.target.value })} />
            </Field>
            <Field label="Guaranteed amount">
              <Input type="number" min="0" step="0.01" value={bg.amount}
                     onChange={(e) => setBg({ ...bg, amount: e.target.value })} />
            </Field>
            <Field label="Expires on"
                   hint="A guarantee with no expiry on file is one nobody will remember to call in.">
              <Input type="date" value={bg.expires}
                     onChange={(e) => setBg({ ...bg, expires: e.target.value })} />
            </Field>
          </div>
          {bgStarted && !bgComplete && (
            <p className="mt-1 text-[11px] text-amber-700">
              All four are needed, or leave them all blank.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

/* ========================================================================== */
/*  AN ADVANCE PAID BEFORE THE SYSTEM EXISTED                                 */
/* ========================================================================== */

function RegulariseModal({ order, onClose, onDone }: {
  order: PosRow | null; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const today = localIso()
  const [amount, setAmount] = useState('')
  const [paidOn, setPaidOn] = useState(today)
  const [mode, setMode] = useState('NEFT')
  const [ref, setRef] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  if (!order) return null

  const asked = Number(amount) || 0
  const ceiling = n(order.unclaimed)
  const over = asked > ceiling + 0.004
  const ready = asked > 0 && !over && mode && paidOn && note.trim().length >= 15

  const submit = async () => {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_adv_regularise', {
      p_wo_id: order.wo_id, p_amount: asked, p_paid_on: paidOn,
      p_mode: mode, p_reference: ref.trim() || null, p_note: note.trim(),
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not record it.')); return }
    setAmount(''); setNote(''); setRef('')
    onDone()
  }

  return (
    <Modal open onClose={onClose}
           title={`Record an advance paid outside IMS — ${order.wo_id}`}
           subtitle={order.vendor_name ?? order.vendor_id}
           footer={
             <>
               <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
               <button className="btn-warning" onClick={submit} disabled={!ready || busy}>
                 {busy ? 'Recording…' : 'Record it'}
               </button>
             </>
           }>
      <div className="space-y-4">
        <Alert tone="warning" title="This asserts that money left the bank on a day nobody here witnessed">
          It arrives already approved and already paid, because both of those things happened
          elsewhere. It is marked permanently as a regularisation so that no auditor mistakes
          it for a payment this system watched. Your explanation is the whole of its evidence.
        </Alert>

        {n(order.over_recovered) > 0.5 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            Bills on this order have already recovered <strong>{rupees(order.over_recovered)}</strong>{' '}
            of advance against nothing on record. If that is the advance you are recording, this
            is where it gets straightened out.
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount" required
                 error={over ? `The contract leaves only ${rupees(ceiling)}.` : undefined}>
            <Input type="number" min="0" step="0.01" value={amount} invalid={over}
                   onChange={(e) => setAmount(e.target.value)} placeholder={inr(ceiling)} />
          </Field>
          <Field label="The day the money actually went" required>
            <Input type="date" value={paidOn} max={today}
                   onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
          <Field label="How it was paid" required>
            <Select value={mode} onChange={(e) => setMode(e.target.value)}
                    options={['NEFT', 'RTGS', 'IMPS', 'CHEQUE', 'UPI', 'CASH']
                      .map((m) => ({ value: m, label: m }))} />
          </Field>
          <Field label="UTR or cheque number">
            <Input value={ref} onChange={(e) => setRef(e.target.value)} />
          </Field>
        </div>

        <Field label="Where this came from and how you know it was paid" required
               hint="At least a sentence.">
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Paid by head office cheque 004312 on 14-08-2026, before this order was put on IMS; confirmed against the SBI statement for that week." />
        </Field>
        {note.trim().length > 0 && note.trim().length < 15 && (
          <p className="text-xs text-amber-700">A few more words — {15 - note.trim().length} to go.</p>
        )}
      </div>
    </Modal>
  )
}

/* ========================================================================== */
/*  ONE ADVANCE                                                               */
/* ========================================================================== */

export function AdvanceDetail() {
  const { id = '' } = useParams()
  const nav = useNavigate()
  const { profile } = useAuth()
  const toast = useToast()
  const [paying, setPaying] = useState(false)
  const [returning, setReturning] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)

  const { data: rows, loading, refresh } = useQuery<AdvRow>(
    `adv_${id}`,
    () => supabase.from('v_wo_advances').select('*').eq('id', id).limit(1),
    [id], { cacheable: false })

  if (loading) return <Loading label="Reading the advance…" />
  const a = rows[0]
  if (!a) {
    return <EmptyState icon={<AlertTriangle className="h-8 w-8" />} title="No such advance"
                       message={`Nothing here is called ${id}.`} />
  }

  const isDraft = a.status === 'DRAFT' || a.status === 'RETURNED'
  const canRaise = MAY_RAISE.includes(profile?.role ?? '')
  const canPay = MAY_PAY.includes(profile?.role ?? '')
  const iRaisedIt = !!a.created_by && a.created_by === profile?.id
  const canWithdraw = isDraft && (iRaisedIt || ['admin', 'accounts_spoc'].includes(profile?.role ?? ''))
  const awaitingPayment = a.status === 'APPROVED' && !a.is_paid
  const bgMissing = a.bg_required && !a.bg_number

  const act = async (fn: () => PromiseLike<{ error: unknown }>, done: string) => {
    setBusy(true)
    const { error } = await fn()
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'That did not go through.')); return }
    setWithdrawing(false); setReturning(false); setWhy('')
    refresh(); toast.success(done)
  }

  /*
    THE CONTRACTOR'S COPY — 0166.

    One sheet that reads as a SANCTION before the money goes and a PAYMENT
    ADVICE after, so his copy always says what is true today. A draft will not
    print: the edge function refuses it, because a proposal nobody has approved
    should not reach him on a letterhead.
  */
  const printable = ['APPROVED', 'CANCELLED', 'REJECTED'].includes(a.status)
  const download = async () => {
    setBusy(true)
    try {
      await downloadFromFunction('generate-works-money-pdf',
                                 { advance_id: a.id }, `${a.id}.pdf`)
    } catch (e) {
      toast.error(dbMessage(e, 'Could not produce the PDF.'))
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button className="btn-ghost btn-sm mb-1" onClick={() => nav('/service/advances')}>
            <ArrowLeft className="h-4 w-4" /> All advances
          </button>
          <h1 className="text-xl font-bold text-slate-900">{a.id}</h1>
          <p className="text-sm text-slate-500">
            {rupees(a.amount)} to {a.vendor_name} against{' '}
            <Link to={`/service/work-orders/${a.wo_id}`} className="text-amber-700 hover:underline">
              {a.wo_id}
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={a.status} />
          {a.is_paid && <span className="pill-green">paid {ddmmyyyy(a.paid_on)}</span>}
          {awaitingPayment && <span className="pill-amber">awaiting payment</span>}
          {a.paid_outside_ims && <span className="pill-amber">paid outside IMS</span>}
          {printable && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    onClick={() => void download()}
                    title={a.is_paid
                      ? 'Payment advice for the contractor'
                      : 'Sanction letter — the payment advice follows when the money goes'}>
              <FileDown className="h-4 w-4" />
              {a.is_paid ? 'Payment advice' : 'Sanction'}
            </button>
          )}
          {isDraft && canWithdraw && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    onClick={() => { setWhy(''); setWithdrawing(true) }}>
              <Trash2 className="h-4 w-4" /> Withdraw
            </button>
          )}
          {isDraft && canRaise && (
            <button className="btn-primary btn-sm" disabled={busy}
                    onClick={() => void act(
                      () => supabase.rpc('rpc_works_action',
                        { p_doc_type: 'ADV', p_doc_id: a.id, p_action: 'SUBMIT' }),
                      'Sent to the Project Head.')}>
              <Send className="h-4 w-4" /> {busy ? 'Sending…' : 'Submit for approval'}
            </button>
          )}
        </div>
      </div>

      {isDraft && (
        <Alert tone={a.status === 'RETURNED' ? 'warning' : 'info'}
               title={a.status === 'RETURNED'
                 ? 'Sent back to you'
                 : 'This is a draft — nobody has been asked to approve it yet'}>
          {a.status === 'RETURNED'
            ? 'Deal with what the Project Head said, then submit it again.'
            : 'Press Submit for approval and it goes to the Project Head, then the Procurement Manager, then the CMD.'}
          {' '}The {rupees(a.amount)} already counts against what else can be advanced on{' '}
          {a.wo_id} while this sits here — withdraw it if it is not going ahead.
        </Alert>
      )}

      {a.paid_outside_ims && (
        <Alert tone="warning" title="Recorded as paid outside this system">
          {a.regularisation_note}
        </Alert>
      )}

      {awaitingPayment && bgMissing && (
        <Alert tone="error" title="The money cannot go out without the bank guarantee">
          This advance is {rupees(a.amount)}, which is at or above the threshold that needs a
          guarantee. Record its number, bank, amount and expiry before Accounts pays it — that
          paper is the only thing standing behind the money.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="The money">
            <div className="grid gap-3 sm:grid-cols-4">
              <Figure label="This advance" value={rupees(a.amount)} strong />
              <Figure label="Contract allowed" value={rupees(a.entitled_at_raising)} />
              <Figure label="Recovery rate" value={`${inr(a.recovery_pct, 2)}%`}
                      sub="of each bill's gross" />
              <Figure label="Still to recover" value={rupees(a.outstanding_now)}
                      tone={n(a.outstanding_now) > 0 ? 'warn' : 'good'} />
            </div>
            <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <TrendingDown className="mr-1 inline h-3.5 w-3.5" />
              Across this order: <strong>{rupees(a.entitled_now)}</strong> allowed by the
              contract, <strong>{rupees(a.paid_now)}</strong> actually paid,{' '}
              <strong>{rupees(a.outstanding_now)}</strong> still to come back off future bills.
            </p>
          </Card>

          <Card title="What it is for">
            <p className="whitespace-pre-wrap text-sm text-slate-700">{a.purpose}</p>
            {a.remarks && <p className="mt-2 text-xs text-slate-500">{a.remarks}</p>}
            <p className="mt-3 text-xs text-slate-400">
              Raised by {a.raised_by_name ?? 'somebody no longer on the system'}{' '}
              on {ddmmyyyy(a.date_created)}.
              {a.approved_by_name && ` Released by ${a.approved_by_name}.`}
            </p>
            {a.return_reason && <Alert tone="warning" title="Returned">{a.return_reason}</Alert>}
            {a.rejection_reason && <Alert tone="error" title="Rejected">{a.rejection_reason}</Alert>}
          </Card>

          <ApprovalChain docType="ADV" docId={a.id} status={a.status}
                         currentStep={a.current_step} createdBy={a.created_by}
                         onChanged={refresh} />
        </div>

        <div className="space-y-4">
          <Card title="The contract">
            <dl className="space-y-2 text-sm">
              <Row label="Work order" value={
                <Link to={`/service/work-orders/${a.wo_id}`} className="text-amber-700 hover:underline">
                  {a.wo_id}
                </Link>} />
              <Row label="Title" value={a.wo_title ?? '—'} />
              <Row label="Site" value={a.site_name ?? a.site_id} />
              <Row label="Contractor" value={a.vendor_name ?? a.vendor_id} />
              <Row label="PAN" value={a.vendor_pan ?? '—'} />
            </dl>
          </Card>

          <Card title="Bank guarantee">
            {a.bg_number ? (
              <dl className="space-y-2 text-sm">
                <Row label="Number" value={a.bg_number} />
                <Row label="Bank" value={a.bg_bank ?? '—'} />
                <Row label="Guaranteed" value={rupees(a.bg_amount)} />
                <Row label="Expires" value={ddmmyyyy(a.bg_expires_on)} />
                {a.bg_returned_on ? (
                  <>
                    <Row label="Returned" value={ddmmyyyy(a.bg_returned_on)} />
                    <p className="pt-1 text-xs text-emerald-700">
                      <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                      Handed back — the advance was fully recovered.
                    </p>
                  </>
                ) : (
                  <>
                    {a.bg_days_to_expiry !== null && a.bg_days_to_expiry <= 45 && (
                      <p className="text-xs text-amber-700">
                        Expires in {a.bg_days_to_expiry} day{a.bg_days_to_expiry === 1 ? '' : 's'}.
                        Get it extended, or recover the balance first.
                      </p>
                    )}
                    {canPay && (
                      <button className="btn-secondary btn-sm mt-2 w-full"
                              disabled={busy || n(a.outstanding_now) > 0.01}
                              title={n(a.outstanding_now) > 0.01
                                ? 'The advance is not fully recovered yet'
                                : undefined}
                              onClick={() => setReturning(true)}>
                        <ShieldCheck className="h-4 w-4" /> Return the guarantee
                      </button>
                    )}
                  </>
                )}
              </dl>
            ) : a.bg_required ? (
              <p className="text-sm text-red-700">
                <AlertTriangle className="mr-1 inline h-4 w-4" />
                Required and not on file. The money cannot go out until it is.
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                <Scale className="mr-1 inline h-4 w-4" />
                Not required at this value, and none was taken.
              </p>
            )}
          </Card>

          <Card title="Payment">
            {a.is_paid ? (
              <dl className="space-y-2 text-sm">
                <Row label="Paid on" value={ddmmyyyy(a.paid_on)} />
                <Row label="Mode" value={a.mode ?? '—'} />
                <Row label="Reference" value={a.reference_no ?? '—'} />
                <p className="pt-1 text-xs text-emerald-700">
                  <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                  Bills on this order now recover {inr(a.recovery_pct, 2)}% until it is back.
                </p>
              </dl>
            ) : a.status !== 'APPROVED' ? (
              <p className="text-sm text-slate-500">
                <CalendarClock className="mr-1 inline h-4 w-4" />
                Nothing goes out until the Project Head, the Procurement Manager and the CMD
                have all approved it.
              </p>
            ) : canPay ? (
              <>
                <p className="mb-3 text-sm text-slate-600">
                  Approved on {ddmmyyyy(a.approval_date)}. Record it once the money has
                  actually left the bank.
                </p>
                <button className="btn-primary btn-sm w-full" disabled={bgMissing}
                        title={bgMissing ? 'The bank guarantee is not on file' : undefined}
                        onClick={() => setPaying(true)}>
                  <Wallet className="h-4 w-4" /> Record the payment
                </button>
              </>
            ) : (
              <p className="text-sm text-slate-500">Approved and waiting for Accounts to pay it.</p>
            )}
          </Card>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      <Modal open={withdrawing} onClose={() => setWithdrawing(false)}
             title="Withdraw this advance" subtitle={`${a.id} — ${rupees(a.amount)}`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setWithdrawing(false)} disabled={busy}>
                   Keep it
                 </button>
                 <button className="btn-warning" disabled={busy}
                         onClick={() => void act(
                           () => supabase.rpc('rpc_adv_withdraw', { p_id: a.id, p_reason: why.trim() || null }),
                           'Withdrawn.')}>
                   {busy ? 'Withdrawing…' : 'Withdraw it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="info" title="Nothing is deleted">
            It stays on the register as withdrawn, and the {rupees(a.amount)} goes back onto
            what may still be advanced on {a.wo_id}.
          </Alert>
          <Field label="Why" hint="Optional.">
            <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)} />
          </Field>
        </div>
      </Modal>

      <Modal open={returning} onClose={() => setReturning(false)}
             title="Return the bank guarantee"
             subtitle={`${a.bg_number} at ${a.bg_bank}`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setReturning(false)} disabled={busy}>
                   Cancel
                 </button>
                 <button className="btn-primary" disabled={busy}
                         onClick={() => void act(
                           () => supabase.rpc('rpc_adv_return_guarantee',
                             { p_id: a.id, p_returned_on: localIso(),
                               p_note: why.trim() || null }),
                           'Guarantee returned.')}>
                   {busy ? 'Recording…' : 'Record the return'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            The advance on {a.wo_id} has been fully recovered, so the guarantee is no longer
            securing anything and can go back to the contractor.
          </p>
          <Field label="Note" hint="Optional — how it was handed back.">
            <Textarea rows={2} value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="Original returned by hand to the contractor's office, acknowledged." />
          </Field>
        </div>
      </Modal>

      <PayModal advance={paying ? a : null} onClose={() => setPaying(false)}
                onDone={() => { setPaying(false); refresh(); toast.success('Payment recorded.') }} />
    </div>
  )
}

function PayModal({ advance, onClose, onDone }: {
  advance: AdvRow | null; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const today = localIso()
  const [paidOn, setPaidOn] = useState(today)
  const [mode, setMode] = useState('NEFT')
  const [ref, setRef] = useState('')
  const [busy, setBusy] = useState(false)

  if (!advance) return null

  const submit = async () => {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_adv_record_payment', {
      p_id: advance.id, p_paid_on: paidOn, p_mode: mode, p_reference: ref.trim() || null,
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not record the payment.')); return }
    onDone()
  }

  return (
    <Modal open onClose={onClose}
           title={`Record the advance of ${rupees(advance.amount)}`}
           subtitle={`${advance.id} — ${advance.vendor_name ?? advance.vendor_id}`}
           footer={
             <>
               <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
               <button className="btn-primary" onClick={submit} disabled={busy || !mode}>
                 {busy ? 'Recording…' : 'Record it'}
               </button>
             </>
           }>
      <div className="space-y-4">
        <Alert tone="info" title="What this sets off">
          From the moment this is recorded, every running account bill on {advance.wo_id} will
          recover {inr(advance.recovery_pct, 2)}% of its gross value until the{' '}
          {rupees(advance.amount)} is back.
        </Alert>
        <Field label="Paid on" required>
          <Input type="date" value={paidOn} max={today} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <Field label="How it was paid" required>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}
                  options={['NEFT', 'RTGS', 'IMPS', 'CHEQUE', 'UPI']
                    .map((m) => ({ value: m, label: m }))} />
        </Field>
        <Field label="UTR or cheque number"
               hint="Not compulsory, but it is what makes this findable at the bank.">
          <Input value={ref} onChange={(e) => setRef(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */

function Figure({ label, value, sub, strong, tone }: {
  label: string; value: string; sub?: string; strong?: boolean; tone?: 'warn' | 'good'
}) {
  return (
    <div className={`rounded-lg border p-3 ${
      tone === 'warn' ? 'border-amber-200 bg-amber-50'
      : tone === 'good' ? 'border-emerald-200 bg-emerald-50'
      : 'border-slate-200 bg-slate-50'}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`${strong ? 'text-lg font-bold' : 'text-sm font-semibold'} text-slate-900`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-right text-sm text-slate-800">{value}</dd>
    </div>
  )
}

export default AdvanceList
